import { http } from "./http";

/**
 * Customer-side numbers overview row. Mirrors NumberOverviewItemDto in
 * the Aivrmanager.Api NumbersController.
 */
export interface NumberListItem {
  dnisId: number;
  numberType: string;
  number: string;
  application: string;
  userTariffPerCall: number;
  userTariffPerMinute: number;
  currency: string;
  /** Pre-formatted "0.05 per minute" / "0.10 per call" / "-" string from the SP. */
  userTariff: string;
  validFrom: string;
  allowEdit: boolean;
  allowEditDestination: boolean;
  gptDnisId?: number | null;
  countryId?: number | null;
  /** Lower-case ISO-3166 alpha-2 (e.g. "nl"); empty string when unknown. */
  countryIso2: string;
  countryName: string;
  /** Numeric calling code without leading +, e.g. "31" — used to render "+31 20 1234567". */
  countryCode: string;
  /** National area code without country prefix ("20" for Amsterdam). */
  areaCode: string;
  areaCity: string;
  areaState: string;
  /** Pre-resolved label from the API ("Amsterdam", "National", …). Empty when unknown. */
  areaLabel: string;
  /** smxSystems Dnis.scriptTypeId — drives the "Application" tile selection. */
  scriptTypeId: number;
  /** Human-readable script name (e.g. "AgentConnect"). Empty when unknown. */
  scriptName: string;
  /**
   * Logical "what this number routes to" label resolved server-side per
   * scriptType so the list cell can render without N+1 follow-ups:
   * Destination → comma-separated agent labels (cleaned name when set,
   * else raw destination); Huntgroup → comma-separated huntgroup display
   * names (prefix stripped); FlowBuilder → active application name (or
   * "Unassigned"); anything else → empty (SPA falls back to scriptName).
   */
  applicationLabel: string;
}

export interface NumberDetail {
  dnisId: number;
  numberType: string;
  number: string;
  dnisRaw: string;
  dnisExtra: string;
  description: string;
  application: string;
  userTariffPerCall: number;
  userTariffPerMinute: number;
  currency: string;
  userTariff: string;
  tariffPrompt: string;
  validFromUtc: string;
  validTillUtc: string;
  active: boolean;
  maxDuration: number;
  allowEdit: boolean;
  allowEditDestination: boolean;
  gptDnisId?: number | null;
  countryId?: number | null;
  countryIso2: string;
  countryName: string;
  countryCode: string;
  areaCode: string;
  areaCity: string;
  areaState: string;
  areaLabel: string;
  scriptTypeId: number;
  scriptName: string;
}

export interface NumberListResponse {
  items: NumberListItem[];
}

/**
 * One scheduled application assignment for a single number. Each row is
 * either "Active" (running right now), "Scheduled" (will activate in the
 * future) or "Expired" (already replaced by a newer schedule).
 */
export interface NumberScheduleItem {
  applicationDnisId: number;
  applicationId: number;
  name: string;
  stateColor: string;
  stateName: "Active" | "Scheduled" | "Expired" | string;
  allowDelete: boolean;
  validFromUtc: string;
}

export interface NumberScheduleResponse {
  dnisId: number;
  items: NumberScheduleItem[];
}

/**
 * One destination (Agent) that the AgentConnect script will route this
 * number to because its name starts with the digits-only
 * dnisPresentation. <code>displayName</code> is what we render in the
 * tile (server-side stripped of the prefix); <code>agentName</code> is
 * the canonical Agent.name should we ever need it for tooltips.
 */
export interface NumberDestinationItem {
  agentId: number;
  displayName: string;
  agentName: string;
  destination: string;
  statusId: number;
  statusName: string;
}

export interface NumberDestinationsResponse {
  dnisId: number;
  /** Digits-only prefix (e.g. "31341770614") agent names must start with to match. */
  prefix: string;
  items: NumberDestinationItem[];
}

/**
 * One huntgroup that the HuntgroupConnect script will route this number
 * to because its name starts with the digits-only dnisPresentation.
 * Same prefix-match contract as <code>NumberDestinationItem</code> but
 * on <code>HuntGroup.name</code>. <code>assignedOnline</code> /
 * <code>assignedTotal</code> mirror the counts shown in the Huntgroups
 * overview so the tile can surface availability without a second
 * round-trip.
 */
export interface NumberHuntGroupItem {
  huntGroupId: number;
  displayName: string;
  hgName: string;
  assignedTotal: number;
  assignedOnline: number;
}

export interface NumberHuntGroupsResponse {
  dnisId: number;
  /** Same digits-only prefix as the destinations response. */
  prefix: string;
  items: NumberHuntGroupItem[];
}

/**
 * The four well-known script-type ids the aivrmanager exposes via its
 * "Application" dropdown. Anything else is reported as "Custom" (the
 * dropdown locks itself in that case so the existing routing isn't
 * tampered with). Mirrors NumbersController.AllowedScriptTypeIds.
 */
export const SCRIPT_TYPE_IDS = {
  destination: 83,
  huntgroup: 76,
  flowBuilder: 2,
  sipTrunk: 203,
} as const;

export type ScriptTypeId =
  (typeof SCRIPT_TYPE_IDS)[keyof typeof SCRIPT_TYPE_IDS];

export const SCRIPT_TYPE_OPTIONS: ReadonlyArray<{
  id: ScriptTypeId;
  label: string;
  description: string;
}> = [
  {
    id: SCRIPT_TYPE_IDS.destination,
    label: "Destination",
    description: "Route directly to a single destination",
  },
  {
    id: SCRIPT_TYPE_IDS.huntgroup,
    label: "Huntgroup",
    description: "Distribute calls over a group of destinations",
  },
  {
    id: SCRIPT_TYPE_IDS.flowBuilder,
    label: "FlowBuilder",
    description: "Run a scheduled application/flow on incoming calls",
  },
  {
    id: SCRIPT_TYPE_IDS.sipTrunk,
    label: "To SIP Trunk",
    description: "Forward to the customer's own SIP trunk",
  },
];

export function isKnownScriptType(id: number): id is ScriptTypeId {
  return SCRIPT_TYPE_OPTIONS.some((o) => o.id === id);
}

export const numbersApi = {
  list: () => http<NumberListResponse>("/api/numbers"),
  get: (dnisId: number) => http<NumberDetail>(`/api/numbers/${dnisId}`),
  updateScriptType: (dnisId: number, scriptTypeId: ScriptTypeId) =>
    http<NumberDetail>(`/api/numbers/${dnisId}/script-type`, {
      method: "PATCH",
      body: JSON.stringify({ scriptTypeId }),
    }),
  getSchedule: (dnisId: number) =>
    http<NumberScheduleResponse>(`/api/numbers/${dnisId}/schedule`),
  addSchedule: (
    dnisId: number,
    payload: { applicationId: number; validFromUtc?: string | null },
  ) =>
    http<NumberScheduleResponse>(`/api/numbers/${dnisId}/schedule`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  deleteSchedule: (dnisId: number, applicationDnisId: number) =>
    http<NumberScheduleResponse>(
      `/api/numbers/${dnisId}/schedule/${applicationDnisId}`,
      { method: "DELETE" },
    ),
  getDestinations: (dnisId: number) =>
    http<NumberDestinationsResponse>(`/api/numbers/${dnisId}/destinations`),
  getHuntGroups: (dnisId: number) =>
    http<NumberHuntGroupsResponse>(`/api/numbers/${dnisId}/huntgroups`),
};
